import { CurrencyPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { catchError, of, take } from 'rxjs'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import type { ArticleDetailsDto } from '../../../api/responses'
import {
  calculateArticlePurchaseTotalAmount,
  calculateArticlePurchaseVatAmount,
} from '../../../shared/utils/article-purchase-price.utils'

@Component({
  selector: 'dhp-article-purchase-summary-card',
  templateUrl: './article-purchase-summary-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, TranslatePipe],
})
export class ArticlePurchaseSummaryCardComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly articlesService = inject(ArticlesService)

  readonly article = signal<ArticleDetailsDto['Article'] | null>(null)
  readonly purchasePrice = computed(
    () => this.article()?.purchase_price ?? null
  )
  readonly studentPricingApplies = computed(
    () => this.purchasePrice()?.studentPricingApplies === true
  )
  readonly regularSubtotal = computed(
    () => this.purchasePrice()?.amount ?? null
  )
  readonly studentSubtotal = computed(
    () => this.purchasePrice()?.studentAmount ?? null
  )
  readonly hasStudentPrice = computed(() => (this.studentSubtotal() ?? 0) > 0)
  /**
   * A student rate exists but this account is not recognised for it (yet). The
   * account step is where the e-mail address is entered, so this is the last
   * point where saying so can still change what the visitor types.
   */
  readonly showStudentPricingNotice = computed(
    () => this.hasStudentPrice() && !this.studentPricingApplies()
  )
  readonly subtotal = computed(() =>
    this.studentPricingApplies()
      ? this.studentSubtotal()
      : this.regularSubtotal()
  )
  readonly studentTotal = computed(() =>
    calculateArticlePurchaseTotalAmount(
      this.studentSubtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly vatAmount = computed(() =>
    calculateArticlePurchaseVatAmount(
      this.subtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )
  readonly total = computed(() =>
    calculateArticlePurchaseTotalAmount(
      this.subtotal(),
      this.purchasePrice()?.taxPercentage
    )
  )

  constructor() {
    const articleId = this.getArticleIdFromRoute()

    if (articleId === null) {
      return
    }

    this.articlesService
      .getArticleDetails(articleId, 'cache-first')
      .pipe(
        take(1),
        catchError((error: unknown) => {
          console.error('Error loading article purchase summary', error)
          return of(undefined)
        })
      )
      .subscribe(article => {
        this.article.set(article ?? null)
      })
  }

  private getArticleIdFromRoute(): number | null {
    for (const route of [...this.route.pathFromRoot].reverse()) {
      const articleId = route.snapshot.paramMap.get('id')

      if (!articleId) {
        continue
      }

      const parsedArticleId = parseInt(articleId, 10)

      if (!isNaN(parsedArticleId)) {
        return parsedArticleId
      }
    }

    return null
  }
}
